import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Space, Popconfirm, message, Modal, Form, Input, Switch } from 'antd-v5';
import PageHeaderLayout from '../../component/page-header-layout';
import { PlusOutlined } from '@ant-design/icons';
import './index.less';
import Details from './components/details';
import {
  requestJobInfoList,
  requestJobInfoStop,
  requestJobInfoStart,
  requestJobInfoAdd,
  requestJobInfoUpdate,
  requestJobInfoDelete,
} from '../../state/request/jobInfo';

const { Item } = Form;

export const JOB_STATUS = {
  NORMAL: 'NORMAL',
  NONE: 'NONE',
};

const TimedTaskManagement = (props) => {
  const [form] = Form.useForm();
  const [list, setList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [searchParams, setSearchParams] = useState({});
  const pageRef = useRef({
    pageNo: 1,
    pageSize: 10,
    total: 0,
  });

  const getData = useCallback(async () => {
    const res = await requestJobInfoList({
      pageNo: pageRef.current.pageNo,
      pageSize: pageRef.current.pageSize,
      ...searchParams,
    });
    if (res.code === 200) {
      pageRef.current.total = res.data?.totalSize || 0;
      setList(res.data?.pageList || []);
    }
  }, [searchParams]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (!customOpen) {
      form.resetFields();
    }
  }, [customOpen]);

  // 停用启用
  const onConfirm = async (record) => {
    const { jobStatus, id } = record;
    const API = jobStatus === JOB_STATUS.NORMAL ? requestJobInfoStop : requestJobInfoStart;
    const res = await API(id);
    if (res?.code === 200) {
      const txt = jobStatus === JOB_STATUS.NORMAL ? '停用' : '启用';
      message.info(`${txt}任务成功`);
      getData();
    }
  };

  const deleteJob = async (record) => {
    const { id } = record;
    const res = await requestJobInfoDelete(id);
    if (res?.code === 200) {
      message.success('删除成功');
      pageRef.current.pageNo = 1;
      getData();
    }
  };

  const columns = [
    {
      title: '任务ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '任务描述',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: '方法名',
      dataIndex: 'methodName',
    },
    {
      title: 'Cron表达式',
      dataIndex: 'jobCron',
    },
    {
      title: '运行状态',
      dataIndex: 'jobStatus',
      width: 100,
    },
    {
      title: '状态',
      dataIndex: 'jobStatus',
      width: 100,
      render: (jobStatus, record) => (
        <Switch
          checkedChildren='启用'
          unCheckedChildren='停用'
          checked={jobStatus === JOB_STATUS.NORMAL}
          onChange={() => {
            onConfirm(record);
          }}
        />
      ),
    },
    {
      title: '操作',
      dataIndex: 'action',
      align: 'center',
      width: 230,
      render: (_, record) => {
        return (
          <Space wrap>
            <Button
              type='link'
              onClick={() => {
                setPreviewData(record);
                setPreviewOpen(true);
              }}
              style={{ color: '#01b755' }}
            >
              查看
            </Button>
            <Button
              type='link'
              onClick={() => {
                form.setFieldsValue(record);
                setCustomOpen(true);
              }}
              style={{ color: '#01b755' }}
            >
              编辑
            </Button>
            <Popconfirm title={`是否要删除任务`} onConfirm={() => deleteJob(record)}>
              <span className='btn' style={{ color: 'red', padding: '0 15px' }}>
                删除
              </span>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const onSearchFinish = (values) => {
    pageRef.current.pageNo = 1;
    setSearchParams(values);
  };
  const onFinish = async () => {
    const values = await form.validateFields();
    const API = values.id ? requestJobInfoUpdate : requestJobInfoAdd;
    const res = await API(values);
    if (res.code === 200) {
      message.success(`${values.id ? '编辑' : '添加'}成功`);
      setCustomOpen(false);
      getData();
    }
  };

  return (
    <PageHeaderLayout>
      <div className='timed-task-management'>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Form layout='inline' className='header' onFinish={onSearchFinish}>
            <Item label='任务描述' name='description'>
              <Input placeholder='搜索任务描述' />
            </Item>
            <Button type='primary' htmlType='submit'>
              查询
            </Button>
          </Form>
          <Button
            onClick={() => {
              setCustomOpen(true);
            }}
            type='primary'
            icon={<PlusOutlined />}
          >
            新增
          </Button>
        </div>

        <Table
          dataSource={list}
          columns={columns}
          pagination={{
            total: pageRef.current.total,
            pageSize: pageRef.current.pageSize,
            current: pageRef.current.pageNo,
            onChange: (page) => {
              pageRef.current.pageNo = page;
              getData();
            },
          }}
        />

        <Modal
          title='任务详情'
          open={previewOpen}
          destroyOnClose
          onOk={() => setPreviewOpen(false)}
          onCancel={() => setPreviewOpen(false)}
          footer={null}
        >
          <Details data={previewData} />
        </Modal>
        <Modal
          title={`${form.getFieldValue('id') ? '编辑' : '添加'}任务`}
          open={customOpen}
          destroyOnClose
          onOk={() => onFinish()}
          onCancel={() => setCustomOpen(false)}
        >
          <Form labelCol={{ span: 5 }} form={form}>
            <Item label='任务ID' name='id' style={{ display: 'none' }}>
              <Input placeholder='请输入任务ID' />
            </Item>
            <Item
              label='方法名'
              name='methodName'
              rules={[{ required: true, message: '请输入方法名' }]}
            >
              <Input placeholder='请输入方法名' />
            </Item>
            <Item
              label='Cron表达式'
              name='jobCron'
              rules={[{ required: true, message: '请输入Cron表达式' }]}
            >
              <Input placeholder='请输入Cron表达式' />
            </Item>
            <Item
              label='任务描述'
              name='description'
              rules={[{ required: true, message: '请输入任务描述' }]}
            >
              <Input.TextArea placeholder='请输入任务描述' />
            </Item>
          </Form>
        </Modal>
      </div>
    </PageHeaderLayout>
  );
};

export default TimedTaskManagement;
